import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { X, MapPin, Navigation, Search, Check, Loader2 } from 'lucide-react';
import placesService from '../../services/placesService';
import { googleMapsLoaderConfig } from '../../config/googleMapsConfig';

// Use shared loader configuration to ensure consistency
const loaderConfig = {
  ...googleMapsLoaderConfig,
  preventGoogleFontsLoading: true,
};

/**
 * MapPinPicker Component
 * A modal with a draggable map pin for fine-tuning location selection (similar to Ola/Uber)
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the picker modal is open
 * @param {function} props.onClose - Callback when modal is closed
 * @param {function} props.onConfirm - Callback when location is confirmed with { latitude, longitude, address, name }
 * @param {Object} props.initialLocation - Initial location { latitude, longitude } to center the map
 * @param {string} props.title - Title for the picker (e.g., "Select Pickup Location")
 * @param {string} props.pinColor - Color of the pin marker (default: blue)
 */
const MapPinPicker = ({
  isOpen,
  onClose,
  onConfirm,
  initialLocation = null,
  title = 'Select Location',
  pinColor = '#3B82F6' // Blue
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const reverseGeocodeRef = useRef(null);

  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [currentCenter, setCurrentCenter] = useState(null); // Start with null, will be set by geolocation
  const [address, setAddress] = useState('');
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hasInitialLocation, setHasInitialLocation] = useState(!!initialLocation);
  const [isConfirming, setIsConfirming] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader(loaderConfig);

  // Debounce timer ref
  const debounceTimerRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Always get user's current location when modal opens (prioritize live location)
  // Only use initialLocation as fallback if geolocation fails
  useEffect(() => {
    if (isOpen) {
      setIsLoadingLocation(true);
      
      // Always request user's current location first, regardless of initialLocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            console.log('📍 [MapPinPicker] Got user current location:', { lat, lng });
            
            setCurrentCenter({ latitude: lat, longitude: lng });
            setIsLoadingLocation(false);
            
            // If map is already initialized, move it to user's location
            if (mapInstanceRef.current) {
              mapInstanceRef.current.panTo({ lat, lng });
              mapInstanceRef.current.setZoom(17);
              // Use ref to access reverseGeocode
              if (reverseGeocodeRef.current) {
                reverseGeocodeRef.current(lat, lng);
              }
            }
          },
          (error) => {
            console.error('⚠️ [MapPinPicker] Geolocation error:', {
              code: error.code,
              message: error.message,
              error: error
            });
            
            // Provide user-friendly error message
            let errorMessage = 'Unable to get your location';
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = 'Location permission denied. Please enable location access.';
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = 'Location information unavailable.';
                break;
              case error.TIMEOUT:
                errorMessage = 'Location request timed out.';
                break;
            }
            console.warn(`⚠️ [MapPinPicker] ${errorMessage} - Using fallback location`);
            
            // Fallback to initialLocation if provided, otherwise use default location
            const fallbackLocation = initialLocation || { latitude: 23.0225, longitude: 72.5714 };
            setCurrentCenter(fallbackLocation);
            setIsLoadingLocation(false);
            
            // If map is already initialized, move it to fallback location
            if (mapInstanceRef.current) {
              mapInstanceRef.current.panTo({ lat: fallbackLocation.latitude, lng: fallbackLocation.longitude });
              mapInstanceRef.current.setZoom(17);
              // Use ref to access reverseGeocode
              if (reverseGeocodeRef.current) {
                reverseGeocodeRef.current(fallbackLocation.latitude, fallbackLocation.longitude);
              }
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000, // Increased timeout to 10 seconds
            maximumAge: 0 // Always get fresh location, don't use cached
          }
        );
      } else {
        console.warn('⚠️ [MapPinPicker] Geolocation not supported, using fallback location');
        // Fallback to initialLocation if provided, otherwise use default location
        const fallbackLocation = initialLocation || { latitude: 23.0225, longitude: 72.5714 };
        setCurrentCenter(fallbackLocation);
        setIsLoadingLocation(false);
      }
    }
  }, [isOpen, initialLocation]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Reset currentCenter to null so we always get fresh location
      setCurrentCenter(null);
      // Always show "Getting your location..." message initially
      // The geolocation effect above will update it with actual location
      setAddress('Getting your location...');
      setSearchText('');
      setSearchSuggestions([]);
      setIsMapInitialized(false);
      setHasInitialLocation(!!initialLocation);
      setIsConfirming(false);
      
      // Properly clean up previous map instance
      if (mapInstanceRef.current) {
        try {
          window.google.maps.event.clearInstanceListeners(mapInstanceRef.current);
          mapInstanceRef.current = null;
        } catch (error) {
          console.error('Error cleaning up map:', error);
          mapInstanceRef.current = null;
        }
      }
    }
  }, [isOpen, initialLocation]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize map when Google Maps is loaded and modal is open
  // Wait for location to be loaded before initializing to prevent blinking
  useEffect(() => {
    if (!isLoaded || !isOpen || isMapInitialized || !mapRef.current) return;
    
    // Wait for location loading to complete AND we have a valid center before initializing map
    if (isLoadingLocation || !currentCenter || !currentCenter.latitude || !currentCenter.longitude) return;

    const initMap = () => {
      // Extra safety check - ensure currentCenter is valid before initializing
      if (!currentCenter || !currentCenter.latitude || !currentCenter.longitude) {
        console.warn('Cannot initialize map: currentCenter is not valid');
        return;
      }
      
      if (window.google && mapRef.current && !mapInstanceRef.current) {
        try {
          // Clear the map container div to prevent white screen
          if (mapRef.current) {
            mapRef.current.innerHTML = '';
          }
          
          const map = new window.google.maps.Map(mapRef.current, {
            center: {
              lat: currentCenter.latitude,
              lng: currentCenter.longitude
            },
            zoom: 16,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            disableDefaultUI: true,
            zoomControl: true,
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: false,
          });

          mapInstanceRef.current = map;
          setIsMapInitialized(true);

          // Define reverse geocode function inline to avoid hoisting issues
          const doReverseGeocode = async (lat, lng) => {
            setIsLoadingAddress(true);
            try {
              const backendResponse = await placesService.reverseGeocode(lat, lng);
              if (backendResponse.success && backendResponse.data) {
                const data = backendResponse.data;
                const addr = data.formattedAddress || data.address;
                if (addr) {
                  setIsLoadingAddress(false);
                  setAddress(addr);
                  return;
                }
              }
              setIsLoadingAddress(false);
              setAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            } catch (error) {
              console.error('Reverse geocode failed:', error);
              setIsLoadingAddress(false);
              setAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            }
          };

          // Listen for map idle (when user stops dragging)
          map.addListener('idle', () => {
            const center = map.getCenter();
            if (center) {
              const newLat = center.lat();
              const newLng = center.lng();
              setCurrentCenter({ latitude: newLat, longitude: newLng });
              doReverseGeocode(newLat, newLng);
            }
          });

          // Do initial reverse geocode with current center
          doReverseGeocode(currentCenter.latitude, currentCenter.longitude);

        } catch (error) {
          console.error('Error creating map:', error);
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initMap, 100);

    return () => {
      clearTimeout(timer);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [isLoaded, isOpen, isMapInitialized, isLoadingLocation, currentCenter?.latitude, currentCenter?.longitude]);

  // Reverse geocode to get address from coordinates using backend API (Google Maps)
  const reverseGeocode = useCallback(async (lat, lng) => {
    setIsLoadingAddress(true);

    try {
      // Use backend reverse geocode API (which uses Google Maps Geocoding API)
      const backendResponse = await placesService.reverseGeocode(lat, lng);

      if (backendResponse.success && backendResponse.data) {
        const data = backendResponse.data;
        // Use the formatted address from Google Maps
        const addr = data.formattedAddress || data.address;
        if (addr) {
          setIsLoadingAddress(false);
          setAddress(addr);
          return;
        }
      }

      // If backend returns success but no address, show coordinates
      setIsLoadingAddress(false);
      setAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);

    } catch (error) {
      console.error('Reverse geocode failed:', error);
      setIsLoadingAddress(false);
      // Fallback to coordinates if API fails
      setAddress(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
    }
  }, []);

  // Store reverseGeocode in ref so it can be accessed before initialization
  useEffect(() => {
    reverseGeocodeRef.current = reverseGeocode;
  }, [reverseGeocode]);

  // Search for places using the backend placesService
  const searchPlaces = useCallback(async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await placesService.getAutocomplete(query, { country: 'in' });

      if (response.success && response.data && Array.isArray(response.data)) {
        setSearchSuggestions(response.data);
        setShowSuggestions(true);
      } else {
        setSearchSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching places:', error);
      setSearchSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (value.trim() && value.length >= 2) {
      debounceTimerRef.current = setTimeout(() => {
        searchPlaces(value);
      }, 300);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle place selection from search
  const handlePlaceSelect = async (suggestion) => {
    setIsSearching(true);
    try {
      const response = await placesService.getPlaceDetails(suggestion.placeId);

      if (response.success && response.data) {
        const place = response.data;
        const lat = place.location?.latitude || place.latitude;
        const lng = place.location?.longitude || place.longitude;

        if (lat && lng) {
          // Move map to selected location
          if (mapInstanceRef.current) {
            mapInstanceRef.current.panTo({ lat, lng });
            mapInstanceRef.current.setZoom(17);
          }

          setCurrentCenter({ latitude: lat, longitude: lng });
          setAddress(place.formattedAddress || place.address || suggestion.mainText);
        }
      }
    } catch (error) {
      console.error('Error fetching place details:', error);
    } finally {
      setSearchText('');
      setSearchSuggestions([]);
      setShowSuggestions(false);
      setIsSearching(false);
    }
  };

  // Handle confirm button click
  const handleConfirm = async () => {
    // Prevent double-clicks
    if (isConfirming) return;
    
    // Don't allow confirming if we don't have a valid location
    if (!currentCenter || !currentCenter.latitude || !currentCenter.longitude) {
      console.warn('Cannot confirm: No valid location selected');
      return;
    }
    
    setIsConfirming(true);
    
    try {
      // Get the actual current center from the map instance directly
      // This ensures we get the most up-to-date position, even if the 'idle' event hasn't fired yet
      let latitude = currentCenter.latitude;
      let longitude = currentCenter.longitude;
      
      if (mapInstanceRef.current) {
        const center = mapInstanceRef.current.getCenter();
        if (center) {
          latitude = center.lat();
          longitude = center.lng();
        }
      }

      // Check if coordinates have changed significantly from currentCenter
      // If so, we need to get the updated address
      const coordsChanged = currentCenter ? 
        (Math.abs(latitude - currentCenter.latitude) > 0.0001 || 
         Math.abs(longitude - currentCenter.longitude) > 0.0001) : false;
      
      let finalAddressText = address;
      
      // If coordinates changed (user moved map and clicked confirm before 'idle' fired),
      // get the updated address for the new location
      if (coordsChanged && !isLoadingAddress) {
        try {
          const backendResponse = await placesService.reverseGeocode(latitude, longitude);
          if (backendResponse.success && backendResponse.data) {
            const data = backendResponse.data;
            finalAddressText = data.formattedAddress || data.address || finalAddressText;
          }
        } catch (error) {
          console.error('Error getting updated address on confirm:', error);
          // Continue with existing address
        }
      }

      // Ensure we have a valid address (not placeholder text)
      const isPlaceholderText = finalAddressText === 'Move the map to select a location' || !finalAddressText || finalAddressText.trim() === '';
      const isCoordinatesOnly = /^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(finalAddressText.trim());

      // If address is placeholder or empty, try to get it one more time or use coordinates
      let finalAddress = finalAddressText;
      if (isPlaceholderText || (!finalAddressText || finalAddressText.trim() === '')) {
        // Try to get address one more time if we don't have it
        if (!isLoadingAddress) {
          try {
            const backendResponse = await placesService.reverseGeocode(latitude, longitude);
            if (backendResponse.success && backendResponse.data) {
              const data = backendResponse.data;
              finalAddress = data.formattedAddress || data.address || `Location at ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
            } else {
              finalAddress = `Location at ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
            }
          } catch (error) {
            console.error('Error getting address on confirm:', error);
            finalAddress = `Location at ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
          }
        } else {
          // If still loading, use coordinates as fallback
          finalAddress = `Location at ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        }
      }

      // Extract first 3 parts of address for a meaningful short name
      // e.g., "4, Paldi, Ahmedabad" from "4, Paldi, Ahmedabad, Gujarat 380006, India"
      let locationName;
      if (isCoordinatesOnly || isPlaceholderText) {
        locationName = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      } else {
        const addressParts = finalAddress.split(',').map(part => part.trim());
        // Take first 3 parts for a meaningful name
        locationName = addressParts.slice(0, 3).join(', ');
      }

      // Ensure finalAddress is never empty or placeholder
      if (!finalAddress || finalAddress === 'Move the map to select a location' || finalAddress.trim() === '') {
        finalAddress = `Location at ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
      }

      onConfirm({
        latitude: latitude,
        longitude: longitude,
        address: finalAddress,
        name: locationName
      });
      handleClose();
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle close
  const handleClose = () => {
    // Properly clean up map instance before closing
    if (mapInstanceRef.current) {
      try {
        window.google.maps.event.clearInstanceListeners(mapInstanceRef.current);
      } catch (error) {
        console.error('Error cleaning up map on close:', error);
      }
    }
    
    setIsMapInitialized(false);
    mapInstanceRef.current = null;
    setSearchText('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
    setAddress('');
    setIsConfirming(false);
    onClose();
  };

  // Get current location using browser geolocation
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          if (mapInstanceRef.current) {
            mapInstanceRef.current.panTo({ lat, lng });
            mapInstanceRef.current.setZoom(17);
          }

          setCurrentCenter({ latitude: lat, longitude: lng });
          // Use ref to access reverseGeocode
          if (reverseGeocodeRef.current) {
            reverseGeocodeRef.current(lat, lng);
          }
        },
        (error) => {
          console.error('Error getting current location:', error);
          alert('Unable to get your current location. Please enable location access.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl h-[600px] bg-white rounded-2xl shadow-2xl overflow-hidden m-4">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-20 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between p-4">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="px-4 pb-4" ref={suggestionsRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchText}
                onChange={handleSearchChange}
                onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Search for a location..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {isSearching && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 animate-spin" />
              )}
            </div>

            {/* Search Suggestions */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute left-4 right-4 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto z-30">
                {searchSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.placeId}
                    onClick={() => handlePlaceSelect(suggestion)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b last:border-b-0 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {suggestion.mainText}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {suggestion.secondaryText}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Map Container */}
        <div className="absolute inset-0 pt-32 pb-32">
          {!isLoaded || isLoadingLocation ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                <p className="text-gray-600 mt-2">
                  {isLoadingLocation ? 'Getting your location...' : 'Loading map...'}
                </p>
              </div>
            </div>
          ) : loadError ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <p className="text-red-600">Error loading map</p>
            </div>
          ) : (
            <div ref={mapRef} className="w-full h-full" />
          )}

          {/* Center Pin (fixed position) */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none z-10">
            <div className="relative">
              {/* Pin shadow */}
              <div
                className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-4 h-2 bg-black/20 rounded-full blur-sm"
              />
              {/* Pin */}
              <svg width="40" height="50" viewBox="0 0 40 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20 0C8.954 0 0 8.954 0 20c0 11.046 20 30 20 30s20-18.954 20-30C40 8.954 31.046 0 20 0z"
                  fill={pinColor}
                />
                <circle cx="20" cy="18" r="8" fill="white" />
              </svg>
              {/* Pulse animation */}
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full animate-ping opacity-30"
                style={{ backgroundColor: pinColor }}
              />
            </div>
          </div>

          {/* Current Location Button */}
          <button
            onClick={getCurrentLocation}
            className="absolute bottom-4 right-4 p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors z-10"
            title="Use current location"
          >
            <Navigation className="w-5 h-5 text-blue-600" />
          </button>
        </div>

        {/* Footer with Address and Confirm Button */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20">
          {/* Selected Address */}
          <div className="flex items-start gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${pinColor}20` }}
            >
              <MapPin className="w-5 h-5" style={{ color: pinColor }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 mb-1">Selected Location</p>
              {isLoadingAddress ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  <span className="text-sm text-gray-500">Getting address...</span>
                </div>
              ) : (
                <p className="text-sm font-medium text-gray-900 line-clamp-2">
                  {address || 'Move the map to select a location'}
                </p>
              )}
            </div>
          </div>

          {/* Confirm Button */}
          <button
            onClick={handleConfirm}
            disabled={!currentCenter || !currentCenter.latitude || !currentCenter.longitude || isLoadingAddress || isConfirming}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all"
          >
            {isConfirming ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Confirming...
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                Confirm Location
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapPinPicker;
